import { ReactNode, createContext, useContext, useMemo, useRef } from "react";
import {
  DraftPlanTaskInput,
  DraftScheduleInput,
  DraftTradePartnerAvailabilityRequestInput,
  InputMaybe,
  SaveBillsInput,
  SavePlanTaskScheduleFlagInput,
  ScheduleFlagReasonType,
} from "src/generated/graphqlTypes";
import { maybeGetTaskToDelay } from "src/routes/projects/dynamic-schedules/draft-mode/DelayFlagPublishStep";
import {
  DraftPlanScheduleQuery,
  ScheduleDraftMode_NewlyCompletedPlanTaskFragment,
  ScheduleDraftMode_PlanTaskFragment,
} from "src/routes/projects/dynamic-schedules/draft-mode/ScheduleDraftMode.generated";
import { Temporal } from "temporal-polyfill";
import { StoreApi, createStore, useStore } from "zustand";

const LAST_UPDATED_NOTIFICATION_TIMEOUT = 6_000;

export type DraftTasksWithChanges = {
  draftTask: ScheduleDraftMode_PlanTaskFragment;
  initialValues: {
    initialStartDate: Temporal.PlainDate | undefined;
    initialEndDate: Temporal.PlainDate | undefined;
    initialDuration: number | undefined;
  };
};

export type UserAddedScheduleFlagsInput = Pick<
  SavePlanTaskScheduleFlagInput,
  "clientId" | "reasonId" | "durationInDays" | "id"
> & {
  taskId: string;
  taskName: string;
  title: string;
  scheduleFlagReasonType: { name: string; code: ScheduleFlagReasonType };
};

export type DraftScheduleStoreState = {
  draftTaskChanges: DraftPlanTaskInput[];
  addDraftTaskChanges: (input: Omit<DraftPlanTaskInput, "desiredEndDate">[]) => void;
  initialTaskValues?: Record<string, ScheduleDraftMode_PlanTaskFragment>;
  maybeSetInitialValues: (input: DraftPlanScheduleQuery) => void;
  getInitialTaskValue: (taskId: string) => ScheduleDraftMode_PlanTaskFragment | undefined;
  reset: () => void;
  resetRequiredTaskScheduleFlag: (requiredFlags: SavePlanTaskScheduleFlagInput[]) => void;
  requiredScheduleFlags: SavePlanTaskScheduleFlagInput[];
  setRequiredScheduleFlags: (input: SavePlanTaskScheduleFlagInput[]) => void;
  userAddedScheduleFlags: UserAddedScheduleFlagsInput[];
  setUserAddedScheduleFlags: (input: UserAddedScheduleFlagsInput[]) => void;
  removeUserAddedScheduleFlags: (clientId: string) => void;
  // A record of validation errors keyed by the ID of whatever ObjectState gave the error
  validationErrors: Record<string, string[]>;
  setValidationErrors: (input: Record<string, string[]>) => void;
  lastUpdatedTaskId: InputMaybe<string>;
  draftTaskTradeC2PInput: SaveBillsInput;
  setDraftTaskTradeC2PInput: (input: SaveBillsInput) => void;
  tparAvailabilityRequestInput: DraftTradePartnerAvailabilityRequestInput[];
  setTparAvailabilityRequestInput: (input: DraftTradePartnerAvailabilityRequestInput[]) => void;
  // Uses publish step getters to determine if the publish flow can be skipped
  canSkipPublishFlow: (
    latestDraftTaskChanges: ScheduleDraftMode_PlanTaskFragment[],
    newlyCompletedPlanTasks: ScheduleDraftMode_NewlyCompletedPlanTaskFragment[],
    tradeConfirmPlanTasks: { id: string }[],
  ) => boolean;
  // Common getters for publishing step
  // Returns array sorted by startDate
  getTasksWithDateOrDurationChanges: (
    latestDraftTaskChanges: ScheduleDraftMode_PlanTaskFragment[],
  ) => DraftTasksWithChanges[];

  scheduleParentName: string;
  skipStaleTaskAutoPin: boolean;
  setSkipStaleTaskAutoPin: (value: boolean) => void;
  currentDate: Temporal.PlainDate | undefined;
  setCurrentDate: (value: Temporal.PlainDate | undefined) => void;
};

type ScheduleDraftStore = StoreApi<DraftScheduleStoreState>;
export type MockInitialValues = Pick<
  DraftScheduleStoreState,
  | "draftTaskChanges"
  | "initialTaskValues"
  | "userAddedScheduleFlags"
  | "requiredScheduleFlags"
  | "draftTaskTradeC2PInput"
  | "tparAvailabilityRequestInput"
>;
type DraftScheduleStoreProviderProps = {
  children: ReactNode;
  // Override the store initial state for testing purposes
  mockInitialValues?: MockInitialValues;
  // The name of the schedule's parent (Project / Development) to help users maintain context while publishing
  scheduleParentName: string;
};

const StoreContext = createContext<ScheduleDraftStore | undefined>(undefined);

export function DraftScheduleStoreProvider({
  children,
  mockInitialValues,
  scheduleParentName,
}: DraftScheduleStoreProviderProps) {
  const storeRef = useRef<ScheduleDraftStore>();

  if (!storeRef.current) {
    storeRef.current = createDraftScheduleStore(scheduleParentName, mockInitialValues);
  }

  return <StoreContext.Provider value={storeRef.current}>{children}</StoreContext.Provider>;
}

function createDraftScheduleStore(scheduleParentName: string, mockInitialValues?: MockInitialValues) {
  const {
    draftTaskChanges: mockDraftTaskChanges,
    initialTaskValues: mockInitialTaskValues,
    requiredScheduleFlags: mockRequiredScheduleFlags,
    userAddedScheduleFlags: mockUserAddedScheduleFlags,
    draftTaskTradeC2PInput: mockDraftTaskTradeC2PInput,
    tparAvailabilityRequestInput: mockTparAvailabilityRequestInput,
  } = mockInitialValues ?? {};

  return createStore<DraftScheduleStoreState>((set, get) => ({
    /**
     * This queue of "draft" task changes will be persist for the duration of the "draft" session. Each change will push a new value into the queue
     * and the server will evaluate each change in the order provided
     */
    draftTaskChanges: mockDraftTaskChanges ?? [],
    // It's important that new changes are always added to the end of the list as the inputs are processed in order.
    addDraftTaskChanges: (input) => {
      set({
        draftTaskChanges: [...get().draftTaskChanges, ...input],
        lastUpdatedTaskId: input.first?.id ?? input.first?.clientId,
      });

      // Automatically clear the lastUpdatedTaskId after a short delay
      setTimeout(() => {
        set({ lastUpdatedTaskId: null });
      }, LAST_UPDATED_NOTIFICATION_TIMEOUT);
    },

    /** This is a map of the initial values of the tasks, keyed by the task id.
     * The "maybeSet" action will check for existing values first so it is safe to call multiple times.
     */
    initialTaskValues: mockInitialTaskValues ?? undefined,
    maybeSetInitialValues: (queryResult) => {
      if (!get().initialTaskValues) {
        set({ initialTaskValues: queryResult.draftPlanSchedule.planTasks.keyBy((t) => t.id) });
      }
    },
    getInitialTaskValue: (taskId) => get().initialTaskValues?.[taskId],

    // Clear the store state which will force-fetch the latest from the server
    reset: () =>
      set({
        draftTaskChanges: [],
        initialTaskValues: undefined,
        skipStaleTaskAutoPin: false,
        currentDate: undefined,
        draftTaskTradeC2PInput: { bills: [] },
        tparAvailabilityRequestInput: [],
      }),
    // Clears out required schedule flags
    resetRequiredTaskScheduleFlag: (requiredFlags) => {
      // Create a set of required flag IDs for easy lookup
      const requiredFlagIds = new Set(requiredFlags.map((flag) => flag.clientId));
      return set({
        draftTaskChanges: [
          ...get().draftTaskChanges.map(({ scheduleFlags, ...others }) => ({
            ...others,
            // Update the draftTaskChanges by filtering out required flags
            scheduleFlags: scheduleFlags?.filter((flag) => !requiredFlagIds.has(flag.clientId)),
          })),
        ],
      });
    },
    requiredScheduleFlags: mockRequiredScheduleFlags ?? [],
    setRequiredScheduleFlags: (input) => set({ requiredScheduleFlags: input }),
    userAddedScheduleFlags: mockUserAddedScheduleFlags ?? [],
    setUserAddedScheduleFlags: (input) => set({ userAddedScheduleFlags: [...get().userAddedScheduleFlags, ...input] }),
    removeUserAddedScheduleFlags: (clientId) =>
      set({
        // Update the user added flags by filtering out deleted flags
        userAddedScheduleFlags: [...get().userAddedScheduleFlags.filter((flag) => flag.clientId !== clientId)],
      }),
    validationErrors: {},
    setValidationErrors: (input) => {
      const osKey = Object.keys(input)[0];
      // Pull out the name of the form state field that errored from the error message
      // ie: "knownDurationInDays: Task duration must be greater than 0 => Task duration must be greater than 0"
      const newErrors = input[osKey].map((error) => error.split(":")[1]);
      const existingErrors = get().validationErrors;
      // Rows mount and unmount as the virtual table scrolls, and each mount reports its errors again.
      // Skip unchanged errors so those reports don't re-render the page, which can loop.
      const currentErrors = existingErrors[osKey];
      if (
        currentErrors &&
        currentErrors.length === newErrors.length &&
        currentErrors.every((e, i) => e === newErrors[i])
      ) {
        return;
      }
      set({ validationErrors: { ...existingErrors, [osKey]: newErrors } });
    },
    lastUpdatedTaskId: null,
    draftTaskTradeC2PInput: mockDraftTaskTradeC2PInput ?? { bills: [] },
    setDraftTaskTradeC2PInput: (input) => set({ draftTaskTradeC2PInput: input }),
    tparAvailabilityRequestInput: mockTparAvailabilityRequestInput ?? [],
    setTparAvailabilityRequestInput: (input) => set({ tparAvailabilityRequestInput: input }),
    canSkipPublishFlow: (latestDraftTaskChanges, newlyCompletedPlanTasks, tradeConfirmPlanTasks) => {
      // NOTE: Though this step can be skipped based on this, users do miss out on reviewing what has changed
      const canSkipDelayFlagStep =
        !maybeGetTaskToDelay(get().getTasksWithDateOrDurationChanges(latestDraftTaskChanges)) &&
        get().userAddedScheduleFlags.isEmpty;

      const canSkipTradeConfirmStep = tradeConfirmPlanTasks.isEmpty;

      const canSkipPayTradesStep = newlyCompletedPlanTasks.every((ncpt) => ncpt.protoBills.isEmpty);

      return canSkipDelayFlagStep && canSkipTradeConfirmStep && canSkipPayTradesStep;
    },
    getTasksWithDateOrDurationChanges: (latestDraftTaskChanges) => {
      return latestDraftTaskChanges
        .map((draftTask) => getInitialValuesIfChanged(draftTask, get().getInitialTaskValue(draftTask.id)))
        .compact()
        .sortBy((taskChange) => taskChange.draftTask.startDate);
    },
    scheduleParentName,
    skipStaleTaskAutoPin: false,
    setSkipStaleTaskAutoPin: (value) => set({ skipStaleTaskAutoPin: value }),
    currentDate: undefined,
    setCurrentDate: (value) => set({ currentDate: value }),
  }));
}

export function useDraftScheduleStore<T>(selector: (state: DraftScheduleStoreState) => T) {
  const store = useContext(StoreContext);

  if (!store) {
    throw new Error("Missing DraftScheduleStoreProvider");
  }

  return useStore(store, selector);
}

// helper functions

function getInitialValuesIfChanged(
  draftTask: ScheduleDraftMode_PlanTaskFragment,
  initialTaskValues?: ScheduleDraftMode_PlanTaskFragment,
) {
  if (!initialTaskValues) return undefined;

  const initialStartDate = initialTaskValues.startDate.equals(draftTask.startDate)
    ? undefined
    : initialTaskValues.startDate;
  const initialEndDate = initialTaskValues.endDate.equals(draftTask.endDate) ? undefined : initialTaskValues.endDate;
  const initialDuration =
    initialTaskValues.durationInDays === draftTask.durationInDays ? undefined : initialTaskValues.durationInDays;

  if (initialStartDate || initialEndDate || initialDuration) {
    return { draftTask, initialValues: { initialStartDate, initialEndDate, initialDuration } };
  }

  // If there are no date or duration changes, return undefined for easy filtering
  return undefined;
}

/** The input for the draft schedule queries: the draft's changes and settings from the store. */
export function useDraftScheduleInput(scheduleParentId: string): DraftScheduleInput {
  const draftTaskChanges = useDraftScheduleStore((state) => state.draftTaskChanges);
  const userAddedScheduleFlags = useDraftScheduleStore((state) => state.userAddedScheduleFlags);
  const skipStaleTaskAutoPin = useDraftScheduleStore((state) => state.skipStaleTaskAutoPin);
  const currentDate = useDraftScheduleStore((state) => state.currentDate);
  return useMemo(
    () => ({
      scheduleParentId,
      draftTaskChanges: mapToDraftPlanTaskInput(draftTaskChanges, userAddedScheduleFlags),
      skipStaleTaskAutoPin,
      currentDate,
    }),
    [scheduleParentId, draftTaskChanges, userAddedScheduleFlags, skipStaleTaskAutoPin, currentDate],
  );
}

/** Maps scheduleStore inputs into the shape the draft query resolver needs */
export function mapToDraftPlanTaskInput(
  draftTaskChanges: DraftPlanTaskInput[],
  userAddedScheduleFlags: UserAddedScheduleFlagsInput[],
): DraftPlanTaskInput[] {
  return [
    ...draftTaskChanges,
    ...userAddedScheduleFlags.map(({ taskId, taskName, title, scheduleFlagReasonType, ...others }) => ({
      id: taskId,
      scheduleFlags: [{ ...others }],
    })),
  ];
}
